import { type Room } from "../providers/roomsProvider";
import {
  Create,
  Edit,
  maxLength,
  NumberInput,
  required,
  Resource,
  TextInput,
  Toolbar,
  ToolbarProps,
  useCreateContext,
  useGetList,
  useRedirect,
} from "react-admin";
import Box from "@mui/material/Box";
import { BackofficeRecordTitle } from "../misc/BackofficeRecordTitle";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { Column } from "../misc/Column";
import { useRecordAndSwitchDrivingSchoolIfNecessary } from "../hooks/useRecordAndSwitchDrivingSchoolIfNecessary";
import { SelectInput } from "../inputs/SelectInput";
import { LoadingIndicator } from "../misc/LoadingIndicator";
import { grants } from "../backoffice.access_control";
import { DeleteRecordButton } from "../buttons/DeleteRecordButton";
import { Form2 } from "../misc/Form2";
import { overviewCardStyle } from "../misc/backofficeTheme";
import { useCurrentDrivingSchoolId } from "../hooks/useCurrentDrivingSchoolId";
import { Branch } from "../providers/branchesProvider";
import { SaveButton2 } from "../buttons/SaveButton2";
import { DefaultList } from "../misc/DefaultList";
import { RecordDetailsHeader } from "../misc/RecordDetailsHeader";

function CreateRoomForm() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const redirect = useRedirect();

  if (!drivingSchoolId) {
    return (
      <Box className="centered">
        <Column sx={{ alignItems: "center" }}>Bitte wähle eine Fahrschule aus.</Column>
      </Box>
    );
  }

  return (
    <Create
      resource="rooms"
      title={<BackofficeRecordTitle />}
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Räume erfolgreich erstellt.");
          redirect(`/drivingSchools/${drivingSchoolId}/rooms`);
        },
        onError: (error: Error) => {
          showErrorToast("Fehler beim Erstellen des Raums.");
          console.error("Failed to create room.", error);
        },
        meta: { drivingSchoolId },
      }}
    >
      <RoomForm mode="create" />
    </Create>
  );
}

function EditRoomForm() {
  const redirect = useRedirect();
  const room = useRecordAndSwitchDrivingSchoolIfNecessary<Room>((it) => it.drivingSchoolId);

  if (!room) {
    return <LoadingIndicator />;
  }

  return (
    <Edit
      resource="rooms"
      mutationOptions={{
        onSuccess: () => {
          showSuccessToast("Änderungen erfolgreich gespeichert.");
          redirect(`/drivingSchools/${room.drivingSchoolId}/rooms`);
        },
        onError: (error: unknown) => {
          showErrorToast("Fehler beim Speichern.");
          console.error("Failed to update room.", error);
        },
      }}
      mutationMode="pessimistic"
      title={
        <BackofficeRecordTitle
          header={
            <RecordDetailsHeader
              backLabel="Räume"
              backUrl={`/drivingSchools/${room.drivingSchoolId}/rooms`}
              title={room.name}
            />
          }
        />
      }
    >
      <RoomForm mode="edit" />
    </Edit>
  );
}

function RoomForm(props: { mode: "create" | "edit" }) {
  const { mode } = props;
  return (
    <Form2 warnWhenUnsavedChanges>
      <Column sx={{ padding: overviewCardStyle.padding }}>
        <TextInput label="Name" source="name" autoFocus={mode === "create"} validate={[required(), maxLength(50)]} />
        <TextInput label="Beschreibung" source="description" />
        <NumberInput label="Anzahl Sitzplätze" source="numberOfSeats" validate={required()} />
        <_BranchSelectInput />
      </Column>
      <_Toolbar />
    </Form2>
  );
}

function _BranchSelectInput() {
  const drivingSchoolId = useCurrentDrivingSchoolId();
  const { data: branches = [] } = useGetList<Branch>("branches", { filter: { drivingSchoolId } });
  return (
    <SelectInput
      label="Filiale *"
      source="branchId"
      options={branches.map((it) => [it.id, it.name])}
      validate={(value) => (value ? true : "Bitte wähle eine Filiale aus.")}
      size="small"
    />
  );
}

function _Toolbar(props: ToolbarProps) {
  const { defaultTitle: creatingRecord } = useCreateContext();
  const drivingSchoolId = useCurrentDrivingSchoolId();
  return (
    <Toolbar {...props} sx={{ justifyContent: "space-between" }}>
      {grants.has("Room:edit") && <SaveButton2 />}
      {!creatingRecord && grants.has("Room:delete") && (
        <DeleteRecordButton onSuccessRedirectTo={`/drivingSchools/${drivingSchoolId}/rooms`} />
      )}
    </Toolbar>
  );
}

export const roomsResource = (
  <Resource
    key="rooms"
    name="rooms"
    list={DefaultList}
    create={CreateRoomForm}
    edit={EditRoomForm}
    options={{
      label: "Räume",
      createTitle: "Neuer Raum",
      noEntriesText: "Noch kein Raum hinterlegt.",
      noMatchingEntriesText: "Keine passenden Räume gefunden.",
    }}
    recordRepresentation={(room: Room) => room.name}
  />
);
